import { useCallback, useEffect, useRef, useState } from 'react';
import isNumber from 'lodash/isNumber';
import type { onMediaSrcExpiryErrorPropsT } from '../../../../../common/types/types';
import type { OnMediaLoadingProps } from '../../../../../common/types/types';
import { BackoffMode, RetryBudget } from '../../../../../common/utils/retry-budget';
import { sleep } from '../../../../../common/utils/sleep';
import type { MEDIA_TYPE } from '../constants/media';
import { MEDIA_EVENT_LISTENER } from '../constants/video-events';
import { getMediaCurrentTime } from '../utils/get-media-current-time';
import { useComputedExpiryTime } from './use-computed-expiry-time';
import { useRefetchSignedUrl } from './use-refetch-signed-url';
import { useRefreshSignedUrlBeforeExpiryTimeout } from './use-refresh-signed-url-before-expiry-timeout';
import { useRefreshSignedUrlOnExpiry } from './use-refresh-signed-url-on-expiry';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;
const BACKOFF_MODE = BackoffMode.LINEAR;
type PropsT = {
  initialSrc: string;
  initialFrame: number | null;
  type: MEDIA_TYPE;
  getTimeFromFrameNumber: ((frame: number) => number) | null;
  mediaType: string;
  onSrcChange: (src: string) => void;
  onLoading: (props: OnMediaLoadingProps) => void;
  onError: (error: Error) => void;
  getSignedUrl: (() => Promise<string | undefined>) | null;
  mediaElement: HTMLVideoElement | HTMLAudioElement | null;
  fitToScreen: () => void;
  srcExpirationDuration: number | null;
  onLoadStart?: (videoUrl: string) => void;
};
type ReturnT = {
  src: string;
};
export function useManagedMediaSource({
  initialSrc,
  initialFrame,
  type,
  getTimeFromFrameNumber,
  mediaType,
  onSrcChange,
  onLoading,
  onError,
  getSignedUrl,
  mediaElement,
  fitToScreen,
  srcExpirationDuration,
  onLoadStart
}: PropsT): ReturnT {
  const [src, setSrc] = useState(initialSrc);
  const isSignedUrlRefreshingSupported = !!getSignedUrl;
  const onSignedUrlExpired = useRefetchSignedUrl({
    getSignedUrl
  });
  const srcExpiryTime = useComputedExpiryTime({
    src,
    srcExpirationDuration
  });
  const currentTimeRef = useRef<number | null>(null);
  const retryBudgetRef = useRef(new RetryBudget({
    maxRetries: MAX_RETRIES,
    baseBackoffMs: RETRY_DELAY_MS,
    backoffMode: BACKOFF_MODE
  }));
  const retryBudget = retryBudgetRef.current;
  const onSrcChangeRef = useRef(onSrcChange);
  onSrcChangeRef.current = onSrcChange;
  const onLoadStartRef = useRef(onLoadStart);
  onLoadStartRef.current = onLoadStart;

  // Track the last initialSrc we extracted frames for to avoid re-extraction on signed URL refresh
  const lastExtractedInitialSrcRef = useRef<string | null>(null);
  const initialFrameRef = useRef(initialFrame);
  initialFrameRef.current = initialFrame;
  const onLoadingRef = useRef(onLoading);
  onLoadingRef.current = onLoading;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const getTimeFromFrameNumberRef = useRef(getTimeFromFrameNumber);
  getTimeFromFrameNumberRef.current = getTimeFromFrameNumber;
  const handleLoading = useCallback((props: OnMediaLoadingProps) => {
    onLoadingRef.current(props);
  }, []);
  const handleError = useCallback((error: Error) => {
    onErrorRef.current(error);
  }, []);
  const handleMediaSrcExpiryError = useCallback(async ({
    mediaError,
    skipSourceValidation
  }: onMediaSrcExpiryErrorPropsT) => {
    return retryBudget.ensureInFlight(async () => {
      // Capture the current playback time of the media element prior to refreshing the signed URL.
      const mediaCurrentTime = getMediaCurrentTime(mediaElement);
      if (isNumber(mediaCurrentTime)) {
        currentTimeRef.current = mediaCurrentTime;
      }

      // Clear the media element's loading state when an error occurs.
      // If the error requires refreshing the source (e.g., due to an expired signed URL),
      // `isRefreshingSource` will be set to true as needed based on the error code.
      handleLoading({
        isMediaElementLoading: false,
        isRefreshingSource: true
      });
      try {
        const updatedSrc = await onSignedUrlExpired({
          mediaError,
          currentSrc: src,
          mediaType: mediaType,
          skipSourceValidation,
          retryBudget
        });
        setSrc(updatedSrc);
        onSrcChangeRef.current(updatedSrc);
      } catch (error) {
        fitToScreen();
        handleError(error);
      } finally {
        handleLoading({
          isRefreshingSource: false
        });
      }
    }, async () => {
      const wait = retryBudget.getNextBackoffMs();
      if (!wait) {
        return;
      }
      await sleep(wait);
    });
  }, [src, mediaType, onSignedUrlExpired, fitToScreen, handleLoading, handleError, retryBudget, mediaElement]);
  useRefreshSignedUrlBeforeExpiryTimeout({
    type,
    srcExpiryTime,
    mediaElement: mediaElement,
    onSignedUrlExpired: handleMediaSrcExpiryError,
    isSignedUrlRefreshingSupported
  });
  useRefreshSignedUrlOnExpiry({
    mediaElement: mediaElement,
    onSignedUrlExpiredError: handleMediaSrcExpiryError,
    onError: handleError
  });

  // Handle mediaElement's loading state based on initial loading and buffering.
  useEffect(() => {
    if (!mediaElement) {
      return;
    }
    handleLoading({
      isMediaElementLoading: true
    });

    // Trigger frame extraction before video load starts (only for new tasks, not signed URL refreshes). InitialSrc won't change for the same task. Only src will change for the same task.
    if (lastExtractedInitialSrcRef.current !== initialSrc) {
      lastExtractedInitialSrcRef.current = initialSrc;
      onLoadStartRef.current?.(initialSrc);
    }
    mediaElement.load();

    // This is used to seek the media to required frame when media is loaded for
    // first time or reloaded post signed url expiry.
    const handleInitialCanPlay = () => {
      retryBudget.reset();

      // If initialFrame and getTimeFromFrameNumber is provided, seek to the frame.
      if (initialFrameRef.current && getTimeFromFrameNumberRef.current) {
        const currentTime = getTimeFromFrameNumberRef.current(initialFrameRef.current);
        if (isNumber(currentTime)) {
          mediaElement.currentTime = currentTime;
          return;
        }
      }

      // If currentTimeRef is set, seek to that duration.
      if (isNumber(currentTimeRef.current)) {
        mediaElement.currentTime = currentTimeRef.current;
        return;
      }
    };
    const handleCanPlay = () => {
      handleLoading({
        isMediaElementLoading: false
      });
    };
    const handleStalled = () => {
      handleLoading({
        isMediaElementLoading: true
      });
    };
    const handleTimeUpdate = () => {
      const mediaCurrentTime = getMediaCurrentTime(mediaElement);
      if (isNumber(mediaCurrentTime)) {
        currentTimeRef.current = mediaCurrentTime;
      }
    };
    mediaElement.addEventListener(MEDIA_EVENT_LISTENER.CAN_PLAY, handleInitialCanPlay, {
      once: true
    });
    mediaElement.addEventListener(MEDIA_EVENT_LISTENER.CAN_PLAY, handleCanPlay);
    mediaElement.addEventListener(MEDIA_EVENT_LISTENER.STALLED, handleStalled);
    mediaElement.addEventListener(MEDIA_EVENT_LISTENER.TIME_UPDATE, handleTimeUpdate);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENT_LISTENER.CAN_PLAY, handleInitialCanPlay);
      mediaElement.removeEventListener(MEDIA_EVENT_LISTENER.CAN_PLAY, handleCanPlay);
      mediaElement.removeEventListener(MEDIA_EVENT_LISTENER.STALLED, handleStalled);
      mediaElement.removeEventListener(MEDIA_EVENT_LISTENER.TIME_UPDATE, handleTimeUpdate);
    };
  }, [src, mediaElement, handleLoading, retryBudget, initialSrc]);
  return {
    src
  };
}